import { useEffect } from "react"
import { useSvgRef } from "@mui/x-charts/hooks"

/**
 * Fixes the MUI X Charts tooltip appearing at the top-left corner when the page
 * scrolls under a stationary mouse.
 *
 * MUI's `ChartsTooltipContainer` opens the tooltip on `pointerenter` (via its
 * MoveGesture) but only seeds the popper position from native `pointermove` /
 * `pointerdown` events. On scroll the browser fires `pointerenter` without a
 * `pointermove`, so the tooltip opens anchored to its initial `{0, 0}` position.
 *
 * We seed the position by re-dispatching a synthetic `pointermove` carrying the
 * real coordinates whenever the pointer enters the chart SVG. This feeds MUI's
 * own position tracker so the tooltip renders under the cursor.
 *
 * The synthetic event MUST carry the real `pointerId`. MUI's gesture manager
 * keeps a map of live pointers keyed by that id and only removes an entry on a
 * matching `pointerup`. A default id of 0 therefore registers a pointer that
 * never goes away, which keeps drag gestures alive after the mouse is released
 * and permanently suppresses the hover gesture the tooltip depends on.
 *
 * Must be rendered as a direct child of a chart component. Renders nothing.
 */
const TooltipPositionBridge = () => {
  const svgRef = useSvgRef()

  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return

    const handlePointerEnter = (event: PointerEvent) => {
      if (event.pointerType !== "mouse" && event.pointerType !== "pen") return

      svg.dispatchEvent(
        new PointerEvent("pointermove", {
          pointerId: event.pointerId,
          isPrimary: event.isPrimary,
          clientX: event.clientX,
          clientY: event.clientY,
          pointerType: event.pointerType,
          bubbles: true,
          cancelable: true,
        }),
      )
    }

    svg.addEventListener("pointerenter", handlePointerEnter)

    return () => {
      svg.removeEventListener("pointerenter", handlePointerEnter)
    }
  }, [svgRef])

  return null
}

export default TooltipPositionBridge
